import filter from 'lodash/filter'
import map from 'lodash/map'
import React, { FC, memo } from 'react'

import { ExpandableList } from '@Components/ExpandableList'
import { ExpandableListItem } from '@Components/ExpandableList/Item'
import {
  ProductBundleHeader, ProductBundleSavings,
  ProductBundleTitle,
  ProductBundleWrapper,
} from '@Components/Product/Bundle/styled'
import { IWithMoney, ProductWithQuantity } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

interface ProductBundleProps {
  bundleItems?: ProductWithQuantity[]
  priceDifference?: IWithMoney
}

export const ProductBundle: FC<ProductBundleProps> = memo(({ bundleItems, priceDifference }) => {
  const { t } = useTranslation()

  // In order to "safely" use coercion later on, we'll remove all items without product data first.
  // This is only because the Graph types do not include our "custom generated" properties for
  // some reason.
  const onlyItemsWithProductData = filter(bundleItems, item => !!item.product)

  if (!onlyItemsWithProductData?.length) {
    return null
  }

  return (
    <ProductBundleWrapper>
      <ProductBundleHeader>
        <ProductBundleTitle>
          {t('products:PP_PACKAGE_CONTAINS')}
        </ProductBundleTitle>
        {(priceDifference?.value || 0) > 0 && (
          <ProductBundleSavings>
            {t('products:PP_YOU_SAVE')}
            {' '}
            <strong>{priceDifference?.format}</strong>
          </ProductBundleSavings>
        )}
      </ProductBundleHeader>
      <ExpandableList>
        {map(onlyItemsWithProductData, ({ product: productData, quantity }) => {
          // @ts-ignore - "image" is set dynamically in the query, and does not have a type. ¯\_(ツ)_/¯
          const { id, image = '', name, url = '' } = productData

          return (
            <ExpandableListItem key={id} image={image} link={url || ''} title={name}>
              {(quantity || 0) > 1
                ? t('products:PP_BUNDLE_ITEM_QUANTITY', '{{arg1}}x {{arg2}}', { arg1: quantity, arg2: name })
                : name}
            </ExpandableListItem>
          )
        })}
      </ExpandableList>
    </ProductBundleWrapper>
  )
})
